import { Vector3, Web3DViewer } from '@technology/web3d';
import {
  PotreeManagerModel,
  PotreeManagerPlugin,
} from '@technology/web3d-plugin-potreemanager';
import { SingleCapabilityProviderBase } from './../base/single-provider-base';
import { PointCloudStyling } from '@trimble/types';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  Setter,
} from 'solid-js';
import { debounce } from '@solid-primitives/scheduled';
import { PointCloudStylingCapabilityProvider } from './point-cloud-styling-provider';
import { PointCloudStylingRenderer } from './point-cloud-styling-renderer';
import { Point2D } from '@trimble/utilities';

type PCModel = PotreeManagerModel & { csib?: string };

const DEBOUNCE_WAIT_TIME = 16; // ~60fps for smooth UI updates

export class PointCloudCapabilityProvider extends SingleCapabilityProviderBase<PCModel> {
  private readonly potreeManager: PotreeManagerPlugin;

  public idKey = 'modelId' as const;

  public isPointCloudsVisible: Accessor<boolean>;
  public setIsPointCloudsVisible: Setter<boolean>;

  /**
   * Render opacity of this viewer's point clouds, from 0 (fully
   * transparent) to 1 (opaque). Set it through {@link setOpacity}.
   */
  public readonly opacity: Accessor<number>;
  private readonly setOpacitySignal: Setter<number>;

  /**
   * The coordinate system identifier from loaded point clouds.
   * Returns the first available csib, or undefined if no point clouds have one.
   */
  public readonly csib: Accessor<string | undefined>;

  /**
   * The center positions of all loaded point clouds.
   */
  public readonly centers: Accessor<Point2D[]>;

  private readonly stylingRenderer: PointCloudStylingRenderer;

  /**
   * Debounced styling application to prevent UI lag during rapid color changes.
   * Applies styling to global viewers and all loaded models at ~60fps for smooth performance.
   */
  private readonly debouncedApplyStyling = debounce(
    (styling: PointCloudStyling) => {
      // Get current models without triggering reactive tracking
      const models = this.viewer.getModels(PotreeManagerModel) as PCModel[];

      // Apply styling to all currently loaded models
      for (const model of models) {
        this.stylingRenderer.applyModelStyling(model, styling);
      }

      this.stylingRenderer.applyGlobalStyling(styling);
    },
    DEBOUNCE_WAIT_TIME
  );

  public override cleanup(): Promise<void> {
    return Promise.resolve(undefined);
  }

  constructor(
    viewer: Web3DViewer,
    private readonly stylingProvider?: PointCloudStylingCapabilityProvider
  ) {
    super(viewer);

    const [isPointCloudsVisible, setIsPointCloudsVisible] =
      createSignal<boolean>(true);
    this.isPointCloudsVisible = isPointCloudsVisible;
    this.setIsPointCloudsVisible = setIsPointCloudsVisible;

    const [opacity, setOpacity] = createSignal<number>(1);
    this.opacity = opacity;
    this.setOpacitySignal = setOpacity;

    this.csib = createMemo(() => {
      const items = this.items();
      return items.find((item) => item.csib !== undefined)?.csib;
    });

    this.centers = createMemo(() => {
      const items = this.items();
      return items.map((item) => {
        const center = item.getModelBoundingBox().getCenter(new Vector3());
        return { x: center.x, y: center.y };
      });
    });

    createEffect(() => {
      this.applyModelVisibility();
    });

    const potreeManager = this.viewer.plugins.get(PotreeManagerPlugin);
    if (!potreeManager) {
      throw new Error('PotreeManagerPlugin is not available on the viewer');
    }

    this.potreeManager = potreeManager;
    this.stylingRenderer = new PointCloudStylingRenderer(potreeManager);

    if (this.stylingProvider) {
      createEffect(() => {
        const currentStyling = this.stylingProvider!.pointCloudStyling();
        this.debouncedApplyStyling(currentStyling);
      });
    }
  }

  /**
   * Sets the render opacity of this viewer's point clouds (0..1).
   *
   * A fully transparent cloud (opacity 0) is not drawn, because drawing it
   * produces no visible pixels but still costs a full point render pass on
   * every frame (expensive for dense clouds on slow GPUs). It stays loaded
   * and pickable: picking and snapping run on the loaded point data, not on
   * the drawn frame. Only {@link setIsPointCloudsVisible} makes the clouds
   * unpickable.
   */
  public setOpacity(opacity: number): void {
    this.potreeManager.opacity = opacity;
    this.setOpacitySignal(opacity);
  }

  private applyModelVisibility(): void {
    const visible = this.isPointCloudsVisible();
    const drawn = visible && this.opacity() > 0;
    const models = this.viewer.getModels(PotreeManagerModel);
    for (const model of models) {
      // root.visible only affects rendering; the picker still hits hidden
      // points, so pickability follows the visibility intent alone.
      model.root.visible = drawn;
      model.pickable = visible;
    }
    // HACK: force viewer update
    if (this.stylingProvider) {
      const currentStyling = this.stylingProvider.pointCloudStyling();
      this.stylingRenderer.applyGlobalStyling(currentStyling);
    }
  }

  public async loadPointCloud(
    url: string,
    id: string,
    fitToView: boolean,
    csib?: string,
    reload: boolean = false
  ): Promise<PCModel | null> {
    const currModel = this.viewer.getModel(id, PotreeManagerModel) as PCModel;

    // Return existing model if it is already loaded and reloadModel is false
    if (currModel && !reload) {
      return Promise.resolve(currModel);
    }

    // Check if item is already loading
    if (this.isItemLoading(id)) {
      // Return null to indicate loading is in progress
      return null;
    }

    // Add to loading set
    this.addToLoadingSet(id);

    try {
      // Unload model if it is already loaded
      if (currModel) {
        await this.viewer.unload(id, {
          fitToView: false,
        });
      }

      const model = await this.potreeManager.load(url, {
        modelId: id,
        fitToView: false,
      });

      // Update csib before adding to loaded models.
      const pcModel = model as PCModel;
      pcModel.csib = csib;

      if (!this.addItem(model)) {
        return null;
      }

      this.applyModelVisibility();

      if (this.stylingProvider) {
        this.stylingRenderer.applyModelStyling(
          model,
          this.stylingProvider.pointCloudStyling()
        );
      }

      if (fitToView) {
        await this.viewer.camera.fitToView(model.modelId);
      }

      return model;
    } finally {
      // Always remove from loading set
      this.removeFromLoadingSet(id);
    }
  }

  public async unloadPointCloud(pointCloudId: string): Promise<void> {
    const model = this.viewer.getModel(pointCloudId, PotreeManagerModel);

    if (model == undefined) {
      return;
    }

    await this.viewer.unload(pointCloudId, {
      fitToView: false,
    });

    this.removeItem(model);
    this.removeFromLoadingSet(pointCloudId);
  }

  public async unloadPointClouds(): Promise<void> {
    const unloadPromises = this.viewer
      .getModels(PotreeManagerModel)
      .map((m) => this.unloadPointCloud(m.modelId));

    await Promise.all(unloadPromises);
  }
}
